import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { createPortal } from "react-dom";
import { X } from "lucide-react";

interface FullScreenImageProps {
  src: string;
  alt?: string;
  isOpen: boolean;
  onClose: () => void;
}

/**
 * Full-screen image lightbox, mounted on document.body via portal so it
 * escapes any overflow:hidden ancestor (e.g. the mobile shell).
 * Close via: backdrop tap, X button, or Escape key.
 */
export default function FullScreenImage({ src, alt, isOpen, onClose }: FullScreenImageProps) {
  const { t } = useTranslation();
  // Lock body scroll while open
  useEffect(() => {
    if (!isOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [isOpen]);

  // Close on Escape
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  // Stop native pointer events from bubbling to `document`. The lightbox is
  // portaled to document.body, which is outside any parent Radix Sheet's
  // content tree — without this, Radix's DismissableLayer sees the backdrop
  // click as "outside" and closes the underlying sheet, dropping the user
  // back to the home screen.
  const stop = (e: React.SyntheticEvent) => e.stopPropagation();

  return createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center animate-in fade-in duration-200"
      style={{ backgroundColor: "rgba(0,0,0,0.92)" }}
      onClick={(e) => { stop(e); onClose(); }}
      onPointerDown={stop}
      onPointerDownCapture={stop}
      onMouseDown={stop}
      onPointerUp={stop}
    >
      {/* Close button */}
      <button
        onClick={(e) => { stop(e); onClose(); }}
        aria-label="Close"
        className="absolute top-4 right-4 flex h-9 w-9 items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors z-10"
      >
        <X className="h-5 w-5" />
      </button>

      {/* Image — stop propagation so clicking the image doesn't close */}
      <img
        src={src}
        alt={alt ?? t("alt.memoryImage")}
        onClick={(e) => e.stopPropagation()}
        className="max-h-[90dvh] max-w-[95vw] object-contain rounded-lg select-none"
        draggable={false}
      />
    </div>,
    document.body,
  );
}
